// src/store/slices/receiptApprovalsSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// HELPERS
// ============================================================

const getErrorMessage = (
  error,
  fallback =
    "Something went wrong"
) => {
  return (
    error?.response?.data
      ?.message ||
    error?.message ||
    fallback
  );
};

// ============================================================
// GET PENDING RECEIPTS
//
// GET
// /api/v1/panchayath-billing/receipts/pending
// ============================================================

export const fetchPendingReceiptsThunk =
  createAsyncThunk(
    "receiptApprovals/list",
    async (
      filters = {},
      {
        rejectWithValue,
      }
    ) => {
      try {
        const params = {
          page:
            filters.page || 1,

          pageSize:
            filters.pageSize ||
            50,

          pan_id:
            filters.pan_id || "",

          search:
            filters.search || "",
        };

        const response =
          await api.get(
            "/api/v1/panchayath-billing/receipts/pending",
            {
              params,
            }
          );

        if (
          !response.data
            ?.success
        ) {
          return rejectWithValue(
            response.data
              ?.message ||
              "Failed to load pending receipts"
          );
        }

        return {
          rows:
            response.data
              ?.data || [],

          pagination:
            response.data
              ?.pagination || {
              page: 1,
              pageSize: 50,
              total: 0,
              totalPages: 0,
            },
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load pending receipts"
          )
        );
      }
    }
  );

// ============================================================
// APPROVE RECEIPT
//
// PUT
// /api/v1/panchayath-billing/receipts/:receiptId/approve
// ============================================================

export const approveReceiptThunk =
  createAsyncThunk(
    "receiptApprovals/approve",
    async (
      receiptId,
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.put(
            `/api/v1/panchayath-billing/receipts/${receiptId}/approve`
          );

        if (
          !response.data
            ?.success
        ) {
          return rejectWithValue(
            response.data
              ?.message ||
              "Failed to approve receipt"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to approve receipt"
          )
        );
      }
    }
  );

// ============================================================
// REJECT RECEIPT
//
// PUT
// /api/v1/panchayath-billing/receipts/:receiptId/reject
// ============================================================

export const rejectReceiptThunk =
  createAsyncThunk(
    "receiptApprovals/reject",
    async (
      {
        receiptId,
        rejection_reason,
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.put(
            `/api/v1/panchayath-billing/receipts/${receiptId}/reject`,
            {
              rejection_reason,
            }
          );

        if (
          !response.data
            ?.success
        ) {
          return rejectWithValue(
            response.data
              ?.message ||
              "Failed to reject receipt"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to reject receipt"
          )
        );
      }
    }
  );

// ============================================================
// INITIAL STATE
// ============================================================

const initialState = {
  rows: [],

  pagination: {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  },

  loading: false,

  approving: false,

  rejecting: false,

  actionReceiptId:
    null,

  error: null,
};

// ============================================================
// SLICE
// ============================================================

const receiptApprovalsSlice =
  createSlice({
    name:
      "receiptApprovals",

    initialState,

    reducers: {
      clearReceiptApprovalError:
        (state) => {
          state.error =
            null;
        },

      resetReceiptApprovals:
        () => initialState,
    },

    extraReducers:
      (builder) => {
        builder

          // ==================================================
          // LIST
          // ==================================================

          .addCase(
            fetchPendingReceiptsThunk.pending,
            (state) => {
              state.loading =
                true;

              state.error =
                null;
            }
          )

          .addCase(
            fetchPendingReceiptsThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.loading =
                false;

              state.rows =
                action.payload
                  ?.rows ||
                [];

              state.pagination =
                action.payload
                  ?.pagination ||
                initialState
                  .pagination;
            }
          )

          .addCase(
            fetchPendingReceiptsThunk.rejected,
            (
              state,
              action
            ) => {
              state.loading =
                false;

              state.error =
                action.payload;
            }
          )

          // ==================================================
          // APPROVE
          // ==================================================

          .addCase(
            approveReceiptThunk.pending,
            (
              state,
              action
            ) => {
              state.approving =
                true;

              state.error =
                null;

              state.actionReceiptId =
                Number(
                  action.meta
                    ?.arg ||
                    0
                ) ||
                null;
            }
          )

          .addCase(
            approveReceiptThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.approving =
                false;

              const receiptId =
                Number(
                  action.payload
                    ?.data
                    ?.receipt_id ||
                    0
                );

              if (
                receiptId
              ) {
                state.rows =
                  state.rows.filter(
                    (row) =>
                      Number(
                        row.receipt_id
                      ) !==
                      receiptId
                  );

                state.pagination.total =
                  Math.max(
                    0,
                    Number(
                      state
                        .pagination
                        .total ||
                        0
                    ) - 1
                  );
              }

              state.actionReceiptId =
                null;
            }
          )

          .addCase(
            approveReceiptThunk.rejected,
            (
              state,
              action
            ) => {
              state.approving =
                false;

              state.actionReceiptId =
                null;

              state.error =
                action.payload;
            }
          )

          // ==================================================
          // REJECT
          // ==================================================

          .addCase(
            rejectReceiptThunk.pending,
            (
              state,
              action
            ) => {
              state.rejecting =
                true;

              state.error =
                null;

              state.actionReceiptId =
                Number(
                  action.meta
                    ?.arg
                    ?.receiptId ||
                    0
                ) ||
                null;
            }
          )

          .addCase(
            rejectReceiptThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.rejecting =
                false;

              const receiptId =
                Number(
                  action.payload
                    ?.data
                    ?.receipt_id ||
                    0
                );

              if (
                receiptId
              ) {
                state.rows =
                  state.rows.filter(
                    (row) =>
                      Number(
                        row.receipt_id
                      ) !==
                      receiptId
                  );

                state.pagination.total =
                  Math.max(
                    0,
                    Number(
                      state
                        .pagination
                        .total ||
                        0
                    ) - 1
                  );
              }

              state.actionReceiptId =
                null;
            }
          )

          .addCase(
            rejectReceiptThunk.rejected,
            (
              state,
              action
            ) => {
              state.rejecting =
                false;

              state.actionReceiptId =
                null;

              state.error =
                action.payload;
            }
          );
      },
  });

// ============================================================
// EXPORT
// ============================================================

export const {
  clearReceiptApprovalError,
  resetReceiptApprovals,
} =
  receiptApprovalsSlice.actions;

export default
  receiptApprovalsSlice.reducer;
